import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import {
	StyledAuthentication, ExcelAuthenticationContainer, SamlAuthenticationIframe, AuthenticationHeader, AuthenticationForm, AuthenticationContent,
	AuthenticateFormInputLogin, AuthenticateFormInputPassword, AuthenticateButton, SamlIframeOrigin } from './styles';
import { AuthenticationState, ConnectionStatus, AuthenticationMode } from './reducer';
import { AuthenticationActionKeys, sendAuthentication, updateCredentials } from './actions';
import { I18n } from 'react-redux-i18n';
import { GalleryInfosState } from '../Gallery/reducer';
const Translate = require('react-redux-i18n').Translate;
import RoutesUtils from '../../utils/routes';
import { Page } from '../Gallery/styles';
import { initializeGallery, setForceSplashscreen } from '../Gallery/actions';
import userAgentUtils from '../../utils/userAgent';
import { createNotification } from '../NotificationBar/actions';
import HeaderLogo from '../MenuManager/Header/Logo';
import AuthUtils from '../../utils/authentication';
import { PostMessageTarget, PostMessageAction } from '../../utils/iframeApi/ReaderApi';
import UrlUtils from '../../utils/url';

export interface AuthenticationProps {
	origin?: SamlIframeOrigin;
	authentication: AuthenticationState;
	galleryInfos: GalleryInfosState;
	dispatch: Dispatch<StoreState>;
}

interface AuthenticationComponentState {
	iframeLocation: string;
}

class Authentication extends React.Component<AuthenticationProps, AuthenticationComponentState> {
	private iframeElement: HTMLIFrameElement;

	constructor(props: AuthenticationProps) {
		super(props);
		this.handleChangeLoginField = this.handleChangeLoginField.bind(this);
		this.handleChangePasswordField = this.handleChangePasswordField.bind(this);
		this.handleExcelAuthentication = this.handleExcelAuthentication.bind(this);
		this.handleSamlAuthentication = this.handleSamlAuthentication.bind(this);
		this.state = {
			iframeLocation: ''
		};
	}

	componentWillMount() {
		const { authentication } = this.props;
		const { userInfos: { mode } } = authentication;

		if (AuthUtils.isSaml(mode)) {
			window.addEventListener('message', this.handleSamlAuthentication);
		}
	}

	componentDidMount() {
		const { authentication } = this.props;
		const { userInfos: { mode, needCredentials } } = authentication;

		if (!needCredentials || AuthUtils.isConnected(mode)) {
			RoutesUtils.historyPush(RoutesUtils.getPathDefault());
		}
	}

	componentWillUnmount() {
		const { authentication } = this.props;
		const { mode } = authentication.userInfos;

		if (AuthUtils.isSaml(mode)) {
			window.removeEventListener('message', this.handleSamlAuthentication);
		}
	}

	componentWillUpdate(nextProps: AuthenticationProps, nextState: AuthenticationComponentState) {
		const { dispatch, authentication, galleryInfos } = this.props;
		const { mode } = authentication.userInfos;

		if (AuthUtils.isSaml(mode)) {
			// Okta redirects to the homepage (in the iframe) after the session cookie is set
			// The redirection needs to be completed before further calls to the ASP are made
			if (nextState.iframeLocation === UrlUtils.getSamlIframeRedirectionUrl(galleryInfos.config)) {
				const isOnCache = AuthUtils.isOnCache(mode);
				if (!isOnCache) {
					const updatedMode = {
						...mode as AuthenticationMode,
						status: isOnCache ? ConnectionStatus.CONNECTED : ConnectionStatus.CONNECTING,
						onCache: isOnCache ? false : undefined
					};

					dispatch(updateCredentials({ mode: updatedMode }));
					dispatch(initializeGallery());
				}
			}
		}
	}

	handleSamlAuthentication = (e) => {
		const { dispatch, galleryInfos, authentication } = this.props;
		const { mode } = authentication.userInfos;
		const { target, action, params } = e.data;

		if (target === PostMessageTarget.GallApp) {
			if (action === PostMessageAction.Authentication && params) {
				const isOnCache = AuthUtils.isOnCache(mode);
				if (params.success) {
					if (!isOnCache) {
						// The splashscreen is forced back to hide the redirection in the iframe (which is a browser version of the gallery)
						dispatch(setForceSplashscreen(true));
					}

					dispatch(updateCredentials({
						login: params.login,
						password: params.password
					}));
				} else {
					if (!isOnCache) {
						dispatch(createNotification('error.login_or_password_incorrect'));
						if (galleryInfos.forceSplashScreen) {
							dispatch(setForceSplashscreen(false));
						}
					} else {
						dispatch(updateCredentials({
							mode: { ...mode as AuthenticationMode, status: ConnectionStatus.NOT_CONNECTED, onCache: false }
						}));
						dispatch(createNotification('error.authentication_needed'));
						RoutesUtils.historyPush(RoutesUtils.getPathAuthentication());
					}
					return;
				}
			}
		}
	}

	handleExcelAuthentication(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const { dispatch, authentication } = this.props;

		if (!userAgentUtils.isOnline()) {
			dispatch(createNotification('info.offline_login'));
			return;
		}

		const { form } = authentication;
		const { login, password } = form;
		dispatch(sendAuthentication(login, password));
	}

	handleChangeLoginField(e: React.FormEvent<HTMLInputElement>) {
		const { dispatch } = this.props;
		dispatch(
			{
				type: AuthenticationActionKeys.SET_FORM_LOGIN,
				value: e.currentTarget.value
			}
		);
	}

	handleChangePasswordField(e: React.FormEvent<HTMLInputElement>) {
		const { dispatch } = this.props;
		dispatch(
			{
				type: AuthenticationActionKeys.SET_FORM_PASSWORD,
				value: e.currentTarget.value
			}
		);
	}

	render() {
		const { authentication, origin } = this.props;
		const { form, userInfos: { mode, login, password } } = authentication;
		const iframeParams = AuthUtils.needReconnecting(mode) ? `?user=${login}&pass=${password}` : ''; // Params are added in case of an automated reauthentication on IOS
		const iframeOrigin = origin === SamlIframeOrigin.App ? SamlIframeOrigin.App : SamlIframeOrigin.Authentication;

		return (
			<StyledAuthentication>
				{ AuthUtils.isSaml(mode)
					? <SamlAuthenticationIframe
						origin={iframeOrigin}
						src={UrlUtils.getSamlAuthenticationPage(iframeParams)}
						innerRef={iframe => {
							if (iframe) {
								this.iframeElement = iframe;
							}
						}}
						onLoad={() => {
							// Change of location in iframe needs to be recorded to know if the redirection has been completed
							if (this.iframeElement.contentWindow) {
								this.setState({ iframeLocation: this.iframeElement.contentWindow.location.href });
							}
						}}
					/>
					: <ExcelAuthenticationContainer>
						<AuthenticationHeader>
							<HeaderLogo />
						</AuthenticationHeader>
						<Page>
							<AuthenticationContent>
								<AuthenticationForm onSubmit={this.handleExcelAuthentication}>
									<AuthenticateFormInputLogin
										value={form.login}
										onChange={this.handleChangeLoginField}
										required={true}
										type="text"
										key="login"
										innerRef={(innerRef) => {
											if (innerRef) {
												innerRef.setAttribute('autocorrect', 'none');
												innerRef.setAttribute('autocapitalize', 'off');
												innerRef.setAttribute('spellcheck', 'false');
											}
										}}
										placeholder={I18n.t('messages.placeholder_login')}
									/>
									<AuthenticateFormInputPassword
										value={form.password}
										onChange={this.handleChangePasswordField}
										required={true}
										type="password"
										key="password"
										placeholder={I18n.t('messages.placeholder_password')}
									/>
									<AuthenticateButton
										type="submit"
									>
										<Translate
											value="messages.connect"
										/>
									</AuthenticateButton>
								</AuthenticationForm>
							</AuthenticationContent>
						</Page>
					</ExcelAuthenticationContainer>
				}
			</StyledAuthentication>
		);
	}
}

const mapStateToProps = (state: StoreState) => {
	return {
		authentication: state.authentication,
		galleryInfos: state.galleryInfos,
	};
};

export default connect(mapStateToProps)(Authentication);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Authentication/index.tsx